import ReceiptIcon from 'ext-ui/svg/icon/ReceiptIcon'
import ReceiptMismatchIcon from 'ext-ui/svg/icon/ReceiptMismatchIcon'
import ReceiptMissingIcon from 'ext-ui/svg/icon/ReceiptMissingIcon'
import React from 'react'

export enum ReceiptVerificationStatus {
  Missing = 'MISSING',
  AutoMatched = 'AUTO_MATCHED',
  Verified = 'VERIFIED',
  Verifying = 'VERIFYING',
  NotVerified = 'NOT_VERIFIED',
  Unreadable = 'UNREADABLE',
  Mismatched = 'MISMATCHED',
}

export enum VerificationSummary {
  None = 'NONE',
  Verifying = 'VERIFYING',
  Verified = 'VERIFIED',
  NotVerified = 'NOT_VERIFIED',
}

// Backend contract: each attachment ships with three booleans indicating
// whether the OCR output matched the transaction on each field. The backend
// does NOT send a `reasons[]` array — the client maps each field's boolean to
// a human-readable string using MATCH_FIELD_LABELS below.
export interface ReceiptMatchingStatuses {
  amountMatch: boolean
  merchantNameMatch: boolean
  dateMatch: boolean
}

// Field → text map. Ordering here determines the order of strings in the
// rendered reasons list (row status text + tooltip). To add or rename a field,
// add an entry here and update `ReceiptMatchingStatuses`; every consumer is
// driven by this map.
export const MATCH_FIELD_LABELS: Array<{
  field: keyof ReceiptMatchingStatuses
  positive: string
  negative: string
}> = [
  {
    field: 'amountMatch',
    positive: 'Verified amount',
    negative: 'Amount cannot be verified',
  },
  {
    field: 'dateMatch',
    positive: 'Verified date',
    negative: 'Date cannot be verified',
  },
  {
    field: 'merchantNameMatch',
    positive: 'Verified merchant',
    negative: 'Merchant cannot be verified',
  },
]

// Produce an AttachmentVerification from the raw matchingStatuses booleans.
// All fields true → Verified with each field's positive label.
// Any field false → NotVerified with one negative label per failing field.
export function verificationFromMatchingStatuses(
  matchingStatuses?: ReceiptMatchingStatuses,
): AttachmentVerification | undefined {
  if (!matchingStatuses) return undefined
  const allMatch = MATCH_FIELD_LABELS.every(
    ({ field }) => matchingStatuses[field],
  )
  if (allMatch) {
    return {
      status: ReceiptVerificationStatus.Verified,
      reasons: MATCH_FIELD_LABELS.map(({ positive }) => positive),
    }
  }
  return {
    status: ReceiptVerificationStatus.NotVerified,
    reasons: MATCH_FIELD_LABELS.filter(
      ({ field }) => !matchingStatuses[field],
    ).map(({ negative }) => negative),
  }
}

// Prefer `verification` if backend ever populates it directly; otherwise
// derive from `matchingStatuses`. Callers should use this instead of reading
// `attachment.verification` directly.
export function resolveAttachmentVerification(attachment: {
  verification?: AttachmentVerification
  matchingStatuses?: ReceiptMatchingStatuses
}): AttachmentVerification | undefined {
  return (
    attachment.verification ??
    verificationFromMatchingStatuses(attachment.matchingStatuses)
  )
}

// Rollup status for a transaction from its attachments: Verified if any
// attachment is verified (mixed counts as verified per T6 spec); else
// NotVerified if any attachment resolves to negative; else undefined.
export function rollupStatusFromAttachments(
  attachments?: Array<{
    verification?: AttachmentVerification
    matchingStatuses?: ReceiptMatchingStatuses
  }>,
): ReceiptVerificationStatus | undefined {
  if (!attachments || attachments.length === 0) return undefined
  let anyNegative = false
  for (const a of attachments) {
    const v = resolveAttachmentVerification(a)
    if (!v) continue
    if (
      v.status === ReceiptVerificationStatus.Verified ||
      v.status === ReceiptVerificationStatus.AutoMatched
    ) {
      return ReceiptVerificationStatus.Verified
    }
    if (
      v.status === ReceiptVerificationStatus.NotVerified ||
      v.status === ReceiptVerificationStatus.Mismatched ||
      v.status === ReceiptVerificationStatus.Unreadable
    ) {
      anyNegative = true
    }
  }
  return anyNegative ? ReceiptVerificationStatus.NotVerified : undefined
}

// Per spec T4 — primary verification contract per attachment.
export interface AttachmentVerification {
  status: ReceiptVerificationStatus
  verifiedAt?: string
  reasons: string[]
  source?: 'MANUAL_UPLOAD' | 'AUTOMATCH'
}

export function receiptVerificationStatusToIcon(
  status?: ReceiptVerificationStatus,
  size = 16,
): React.ReactElement {
  switch (status) {
    case ReceiptVerificationStatus.AutoMatched:
    case ReceiptVerificationStatus.Verified:
    case ReceiptVerificationStatus.Verifying:
      return <ReceiptIcon height={size} width={size} />
    case ReceiptVerificationStatus.Mismatched:
    case ReceiptVerificationStatus.NotVerified:
    case ReceiptVerificationStatus.Unreadable:
      return <ReceiptMismatchIcon height={size} width={size} />
    case ReceiptVerificationStatus.Missing:
    default:
      return <ReceiptMissingIcon height={size} width={size} />
  }
}

export function receiptVerificationStatusToTooltip(
  status?: ReceiptVerificationStatus,
): string {
  switch (status) {
    case ReceiptVerificationStatus.AutoMatched:
    case ReceiptVerificationStatus.Verified:
      return 'Receipt verified'
    case ReceiptVerificationStatus.Verifying:
      return 'Verifying receipt…'
    case ReceiptVerificationStatus.NotVerified:
    case ReceiptVerificationStatus.Mismatched:
    case ReceiptVerificationStatus.Unreadable:
      return 'Receipt not verified'
    case ReceiptVerificationStatus.Missing:
    default:
      return 'Receipt missing'
  }
}

// Per T6: the table shows the "Not Verified" icon only when a transaction has
// attachments and none are verified. Verified / AutoMatched / Missing fall
// back to the pre-existing receipts indicator.
export function shouldShowNotVerifiedIcon(
  status?: ReceiptVerificationStatus,
): boolean {
  switch (status) {
    case ReceiptVerificationStatus.NotVerified:
    case ReceiptVerificationStatus.Mismatched:
    case ReceiptVerificationStatus.Unreadable:
    case ReceiptVerificationStatus.Verifying:
      return true
    default:
      return false
  }
}

// Sort key for the Receipts column per EX-39079 T6 spec: flagged transactions
// sort first (ascending) so admins can triage. Higher number = less actionable.
export function verificationSortKey(
  status?: ReceiptVerificationStatus,
): number {
  switch (status) {
    case ReceiptVerificationStatus.NotVerified:
    case ReceiptVerificationStatus.Mismatched:
    case ReceiptVerificationStatus.Unreadable:
      return 0
    case ReceiptVerificationStatus.Verifying:
      return 1
    case ReceiptVerificationStatus.Verified:
    case ReceiptVerificationStatus.AutoMatched:
      return 2
    case ReceiptVerificationStatus.Missing:
    default:
      return 3
  }
}

// Pure presentation mapping: translates the backend's rolled-up
// receiptVerificationStatus into the summary-pill states.
export function statusToVerificationSummary(
  status?: ReceiptVerificationStatus,
): VerificationSummary {
  switch (status) {
    case ReceiptVerificationStatus.Verified:
    case ReceiptVerificationStatus.AutoMatched:
      return VerificationSummary.Verified
    case ReceiptVerificationStatus.Verifying:
      return VerificationSummary.Verifying
    case ReceiptVerificationStatus.NotVerified:
    case ReceiptVerificationStatus.Mismatched:
    case ReceiptVerificationStatus.Unreadable:
      return VerificationSummary.NotVerified
    case ReceiptVerificationStatus.Missing:
    default:
      return VerificationSummary.None
  }
}
